import React, { Fragment, useState } from 'react';
import 'react-lazy-load-image-component/src/effects/blur.css';

import ExperienceSummary from '../../components/ExperienceSummary';
import FieldFillerCard from '../../components/FieldFillerCard';
import { mapBy, notUndefined } from '../../../../../../../../shared/lib/functional';
import { keyFromLabel } from '../../../../../../../../shared/lib/utils';
import { validateField } from '../../components/FieldFillerItem';
import { useForcedCartCache } from '../../context/CartCacheContext';
import { StepPropsBase } from '../Reserve';
import TwoColumnLayout from '../../components/TwoColumnLayout';
import Alert from '../../components/Alert';
import { evaluateAudienceZone } from '../../../../../../../../shared/lib/audience';
import { useForcedEventCache } from '../../context/EventCacheContext';

type TicketEntryViewProps = StepPropsBase;

export default function TicketEntryView(props: TicketEntryViewProps) {
  const { onBack, onNext } = props;
  const { event, allEvents } = useForcedEventCache();
  const { activeDraft: activeDraft, passiveUpdateTicketField, saveCart } = useForcedCartCache();

  const draft = activeDraft.reservationDraft;
  const draftTemplate = draft.eventSnapshot;

  const availableTickets = draftTemplate.reservables.tickets;
  const ticketsMap = mapBy(availableTickets, '_id');

  const myTickets = draft.tickets ?? [];

  const [loading, setLoading] = useState(false);

  const canContinue = myTickets.every(t => {
    const ticket = ticketsMap[t.ticket._id];
    return Array.from({ length: t.quantity }).every((_, j) =>
      ticket.fields.every(f => {
        const value = t.values?.[j]?.[keyFromLabel(f.label)] || '';
        return validateField(f, value).length === 0;
      })
    );
  });

  async function handleOnNext() {
    setLoading(true);

    try {
      if (!canContinue) return;
      await saveCart();
      onNext?.();
    } finally {
      setLoading(false);
    }
  }

  const ticketsWithFields = myTickets
    .map((t, i) => {
      const ticket = ticketsMap[t.ticket._id];
      if (!ticket || (ticket.fields?.length ?? 0) === 0) return undefined;

      return (
        <Fragment key={i}>
          {Array.from({ length: t.quantity }).map((_, j) => {
            const uniqueKey = `${t.ticket._id}-${i}-${j}`;
            const values = t.values?.[j] || {};

            return (
              <FieldFillerCard
                key={uniqueKey}
                title={`${ticket.name} ${j + 1}`}
                fields={ticket.fields}
                values={values}
                onChangeField={(field, value) => {
                  passiveUpdateTicketField(i, j, field.label, value);
                }}
              />
            );
          })}
        </Fragment>
      );
    })
    .filter(notUndefined);

  const dataCollections = allEvents?.collections ?? {
    segments: [],
    levels: [],
    attributes: [],
    zones: [],
  };

  const visibleZones = dataCollections.zones
    .filter(z => evaluateAudienceZone('visible', z._id, undefined, event.eventTemplate.audience))
    .map(z => (
      <div
        key={z._id}
        className="text-white shrink-0 min-w-0 bg-black text-sm font-bold rounded-full px-2 py-0 truncate go-event-zone"
        style={{ backgroundColor: z.color }}
      >
        {z.name}
      </div>
    ));

  return (
    <TwoColumnLayout
      title={draftTemplate?.title}
      tags={visibleZones}
      subtitle="Ticket Details"
      aside={
        <ExperienceSummary
          nextDisabled={!canContinue}
          onBack={() => onBack?.()}
          onNext={handleOnNext}
          loading={loading}
        />
      }
    >
      {ticketsWithFields.length > 0 ? (
        ticketsWithFields
      ) : (
        <Alert>
          No ticket details required for this experience. You can continue to the next step.
        </Alert>
      )}
    </TwoColumnLayout>
  );
}
